// Razorpay Standard Checkout helper.
// Loads the Checkout script once, then exposes payWithRazorpay() that
// creates an order on our backend, opens the modal, and verifies the signature.
import api from "./api";

const SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";
let _scriptPromise = null;

function loadScript() {
  if (_scriptPromise) return _scriptPromise;
  _scriptPromise = new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error("Failed to load Razorpay Checkout"));
    document.body.appendChild(s);
  });
  return _scriptPromise;
}

/**
 * Opens Razorpay Checkout and resolves with the verified payment doc on success.
 * @param {Object} opts
 * @param {number} opts.amount        Amount in INR (rupees)
 * @param {string} opts.purpose       'membership' | 'renewal' | 'event' | 'donation'
 * @param {string} [opts.event_id]
 * @param {Object} [opts.prefill]     { name, email, contact }
 * @param {string} [opts.description]
 */
export async function payWithRazorpay({ amount, purpose = "membership", event_id, prefill = {}, description }) {
  await loadScript();

  // 1. Create order on our backend
  const orderRes = await api.post("/payments/razorpay/order", { amount, purpose, event_id });
  const order = orderRes.data;

  // 2. Open Razorpay checkout modal
  return new Promise((resolve, reject) => {
    const options = {
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: "KELTA",
      description: description || (
        purpose === "membership" ? "KELTA Annual Membership Fee"
        : purpose === "renewal" ? "KELTA Membership Renewal"
        : purpose === "event" ? "KELTA Event Registration"
        : "KELTA Payment"
      ),
      image: `${window.location.origin}/kelta-crest-v2.jpg`,
      order_id: order.order_id,
      prefill: {
        name: prefill.name || "",
        email: prefill.email || "",
        contact: prefill.contact || "",
      },
      notes: { purpose, receipt: order.receipt },
      theme: { color: "#005EB8" },
      modal: {
        ondismiss: () => reject(new Error("PAYMENT_CANCELLED")),
      },
      handler: async (resp) => {
        try {
          const verifyRes = await api.post("/payments/razorpay/verify", {
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
            purpose,
            event_id,
          });
          resolve(verifyRes.data);
        } catch (e) {
          reject(e);
        }
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (r) => {
      reject(new Error(r?.error?.description || "Payment failed"));
    });
    rzp.open();
  });
}

/**
 * Admin-side helper: an admin (finance role) can open Razorpay Checkout on behalf
 * of an unpaid member. Uses the admin-only endpoints that scope by target member
 * (so signature verification updates the correct member's payment_status).
 */
export async function payWithRazorpayOnBehalf({ memberId, amount, purpose = "membership", event_id }) {
  await loadScript();
  const orderRes = await api.post(`/admin/members/${memberId}/razorpay-order`, { amount, purpose, event_id });
  const order = orderRes.data;
  return new Promise((resolve, reject) => {
    const options = {
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: "KELTA",
      description: purpose === "membership" ? "KELTA Annual Membership Fee" : "KELTA Payment",
      image: `${window.location.origin}/kelta-crest-v2.jpg`,
      order_id: order.order_id,
      prefill: order.prefill || {},
      notes: { purpose, receipt: order.receipt, on_behalf_of: memberId },
      theme: { color: "#005EB8" },
      modal: {
        ondismiss: () => reject(new Error("PAYMENT_CANCELLED")),
      },
      handler: async (resp) => {
        try {
          const verifyRes = await api.post("/admin/payments/razorpay-verify", {
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
            purpose,
            event_id,
          });
          resolve(verifyRes.data);
        } catch (e) {
          reject(e);
        }
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (r) => reject(new Error(r?.error?.description || "Payment failed")));
    rzp.open();
  });
}
